import { Injectable, NgZone } from '@angular/core';

import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  fromEventPattern,
  map,
  merge,
  shareReplay,
  startWith
} from 'rxjs';

import { BrowserService } from '../browser/browser.service';
import { Breakpoint, BreakpointKey, BreakpointRangeKey, CssUtils } from '../helpers/css-utils';
import { PlatformService } from '../platform/platform.service';
import { WindowRefService } from '../window-ref/window-ref.service';

@Injectable({
  providedIn: 'root'
})
export class BreakpointService {
  public readonly breakpointChange$: Observable<BreakpointKey>;
  public readonly isDesktopView$: Observable<boolean>;
  public readonly isMobileView$: Observable<boolean>;

  private readonly bpChangeSubject = new Subject<BreakpointKey>();
  private readonly win: Window;
  private readonly isBrowser: boolean;
  private readonly breakpointKeys = Object.values(Breakpoint);
  private currentBreakpoint: BreakpointKey;

  constructor(
    private readonly browserService: BrowserService,
    { isBrowser }: PlatformService,
    { nativeWindow }: WindowRefService,
    private readonly zone: NgZone
  ) {
    this.win = nativeWindow;
    this.isBrowser = isBrowser;

    this.listenToBreakpointChange();
    this.setBreakpoint();

    this.breakpointChange$ = this.bpChangeSubject.asObservable();

    /**
     * The observable emits if within mobile viewport (<=SM).
     * It emits initially on subscription and for any further viewport change.
     * It does NOT emit on each breakpoint change.
     * Also, for performance optimization it's shared for all subscribers.
     */
    this.isDesktopView$ = this.getIsMobileView$(false);
    this.isMobileView$ = this.getIsMobileView$();
  }

  isWithinMinBound(breakpoint: BreakpointKey, width = this.getWinWidth()): boolean {
    const { min } = CssUtils.getBreakpoint(breakpoint);
    return !min || width >= min;
  }

  isWithinMaxBound(breakpoint: BreakpointKey, width = this.getWinWidth()): boolean {
    const { max } = CssUtils.getBreakpoint(breakpoint);
    return !max || width <= max;
  }

  isWithinBounds(breakpoint: BreakpointKey): boolean {
    return this.isWithinBreakpoints(breakpoint, breakpoint);
  }

  isWithinBreakpoints(min: BreakpointKey, max: BreakpointKey): boolean {
    const width = this.getWinWidth();
    const withinMinBound = this.isWithinMinBound(min, width);
    const withinMaxBound = this.isWithinMaxBound(max, width);

    return withinMinBound && withinMaxBound;
  }

  getBreakpoint(): BreakpointKey {
    return this.currentBreakpoint;
  }

  private setBreakpoint(): void {
    const breakpoint = this.breakpointKeys.find((key: BreakpointKey) => this.isWithinBounds(key));

    if (breakpoint !== this.currentBreakpoint) {
      this.currentBreakpoint = breakpoint;
      this.bpChangeSubject.next(breakpoint);
    }
  }

  private listenToBreakpointChange(): void {
    if (this.isBrowser && this.win.matchMedia) {
      const mqObservables = this.breakpointKeys.reduce((arr, key) => {
        const mediaQuery = CssUtils.getMediaQuery(key, BreakpointRangeKey.MIN);

        if (mediaQuery) {
          const queryList = this.win.matchMedia(mediaQuery);
          // deprecated addListener still required for IE and older Safari
          // eslint-disable-next-line
          return [...arr, fromEventPattern<MediaQueryListEvent>(queryList.addListener.bind(queryList))];
        }

        return arr;
      }, []);

      merge(...mqObservables)
        // when resizing across multiple breakpoints at once
        // minimally debounce to ensure breakpoint will be correctly determined afterwards
        .pipe(debounceTime(0))
        .subscribe(() => this.zone.run(() => this.setBreakpoint()));
    }
  }

  private getWinWidth(): number {
    if (this.isBrowser) {
      return this.win.innerWidth;
    }

    return this.browserService.isMobile
      ? CssUtils.getBreakpoint(Breakpoint.XS).max
      : CssUtils.getBreakpoint(Breakpoint.MD).min;
  }

  private getIsMobileView$(mobile = true): Observable<boolean> {
    return this.bpChangeSubject.pipe(
      startWith(this.currentBreakpoint),
      map(_ => this.isWithinMaxBound(Breakpoint.SM)),
      map(isMobile => (mobile ? isMobile : !isMobile)),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }
}
